// ═══════════════════════════════════════════════════════════════════
//  SearchDropdown — Live results popover anchored under the SearchBar.
// ═══════════════════════════════════════════════════════════════════
//
//  Subscribes to SearchContext.query and runs runSearch against the
//  index produced by useSearchIndex. Keeps results in LiveFilterContext
//  so canvas pages can dim non-matching entities.
//
//  Phase D wires atlas-only (atlas data is already loaded by useAtlas
//  on /atlas* routes). Workflow rows arrive in Phase E when the
//  Search Canvas page integrates DB fetching.
//
// ═══════════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import { Pin, PinOff, History } from 'lucide-react';
import { useSearch } from '@/contexts/SearchContext';
import { useLiveFilter } from '@/contexts/LiveFilterContext';
import { useAtlas } from '@/hooks/useAtlas';
import { useSearchCapabilities } from '@/hooks/useSearchCapabilities';
import { useSearchIndex } from '@/hooks/search/useSearchIndex';
import { useSavedSearches } from '@/hooks/search/useSavedSearches';
import { runParsedSearch, runSearch } from '@/lib/search/searchEngine';
import { parseQuery } from '@/lib/search/parseQuery';
import { IS_ATLAS_BUILD } from '@/lib/buildMode';
import type { SearchHit } from '@/types/search';
import { LiveFilterResultsList } from './LiveFilterResultsList';

export function SearchDropdown() {
  const { query, scope, isOpen, pushRecent, recents, setQuery, setScope } = useSearch();
  const { setActiveQuery, setHits } = useLiveFilter();
  const { canQuickSearch } = useSearchCapabilities();
  const { saved, pin, unpin } = useSavedSearches();

  // Atlas isolation policy (see documents/v1/SYSTEM_STATUS.md):
  //   - Build-time: VITE_DISABLE_ATLAS=true strips atlas entirely.
  //   - Runtime: only Pro+ users get atlas hits; Free/Advanced see
  //     workflow-only results (currently empty until Phase E wires DB).
  //
  // Both gates must pass before useAtlas runs, otherwise we'd risk
  // pulling atlas content into a Free user's dropdown.
  const allowAtlasInDropdown = IS_ATLAS_BUILD && canQuickSearch;
  const { atlas } = useAtlas();
  const safeAtlas = allowAtlasInDropdown ? atlas : null;
  const { index, isTruncated, workflowCount, atlasDocCount } = useSearchIndex({
    atlas: safeAtlas,
    workflows: null,
  });

  const hits: SearchHit[] = useMemo(() => {
    if (!query.trim()) return [];
    const parsed = parseQuery(query);
    if (parsed.hasContent) {
      return runParsedSearch(index, parsed, { scope, limit: 200 });
    }
    return runSearch(index, { query, scope, limit: 200 });
  }, [index, query, scope]);

  // Push hits + active query into LiveFilterContext so canvas listeners can react.
  useEffect(() => {
    setActiveQuery(query);
    setHits(hits);
  }, [query, hits, setActiveQuery, setHits]);

  if (!isOpen) return null;

  // #66: tom query + öppen dropdown → visa senaste sökningarna istället
  // för att stänga. SearchBar äger nu close-vägarna (Esc, click-outside,
  // välj träff). Klick på en recent fyller query + scope; SearchBar
  // synkar sitt input-värde från context-query:n.
  if (!query.trim()) {
    if (recents.length === 0) return null;
    return (
      <div
        className="absolute right-0 top-full z-50 mt-1 w-[28rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border bg-popover shadow-lg"
        role="listbox"
        aria-label="Recent searches"
        data-testid="search-recents-panel"
      >
        <div className="flex items-center gap-1.5 border-b px-3 py-2 text-xs text-muted-foreground">
          <History size={12} aria-hidden />
          Senaste sökningar
        </div>
        <ul className="max-h-64 overflow-y-auto py-1">
          {recents.slice(0, 8).map((r) => (
            <li key={`${r.query}·${r.scope}·${r.ranAt}`}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
                onClick={() => {
                  setScope(r.scope);
                  setQuery(r.query);
                }}
                data-testid="search-recent-item"
              >
                <span className="truncate">{r.query}</span>
                <span className="shrink-0 text-[10px] text-muted-foreground">{r.scope}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const totalHits = hits.length;
  const trimmedQuery = query.trim();
  const pinLabel = `${trimmedQuery} · ${scope}`;
  const isPinned = saved.some((s) => s.label === pinLabel);

  function handleTogglePin() {
    if (isPinned) {
      void unpin(pinLabel);
    } else {
      void pin({ label: pinLabel, query: trimmedQuery, scope });
    }
  }

  return (
    <div
      className="absolute right-0 top-full z-50 mt-1 w-[28rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border bg-popover shadow-lg"
      role="listbox"
      aria-label="Search results"
    >
      <div className="flex items-center justify-between border-b px-3 py-2 text-xs">
        <span className="text-muted-foreground">
          {totalHits === 0
            ? 'No matches'
            : totalHits === 1
              ? '1 match'
              : `${totalHits} matches`}
        </span>
        <span className="text-[10px] text-muted-foreground">
          Indexed: {workflowCount} wf · {atlasDocCount} docs
          {isTruncated ? ' · truncated' : ''}
        </span>
      </div>
      <LiveFilterResultsList
        hits={hits}
        onSelect={(hit) => {
          pushRecent({ query, scope, hitCount: totalHits });
          // navigate is handled inside LiveFilterResultsList via openUrl
          // we just record the recent entry here.
          void hit;
        }}
      />
      <div className="flex items-center justify-between border-t px-3 py-1.5 text-[10px] text-muted-foreground">
        <span>↩ Enter for full canvas · ↑↓ to navigate · Esc to close</span>
        <button
          type="button"
          onClick={handleTogglePin}
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] hover:bg-accent"
          title={isPinned ? 'Unpin this search' : 'Pin this search for later'}
          data-fw-search-pin={isPinned ? 'pinned' : 'unpinned'}
        >
          {isPinned ? <PinOff size={10} /> : <Pin size={10} />}
          {isPinned ? 'Unpin' : 'Pin'}
        </button>
      </div>
    </div>
  );
}
